import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Tornado, CloudRain, Wind, Zap, AlertTriangle, CloudSnow, Thermometer, MapPin } from "lucide-react";

const KIND_ICON = {
  tornado: Tornado,
  flood: CloudRain,
  hurricane: AlertTriangle,
  wind: Wind,
  hail: CloudRain,
  ice: CloudSnow,
  thunder: Zap,
  rain: CloudRain,
};

export default function DashboardMarket() {
  const [data, setData] = useState({ tickers: [], chart: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/market");
        setData(data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="market-page">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">module · storm watch</div>
        <h1 className="font-display text-4xl text-white tracking-tight">
          Live <em className="silver-text not-italic">threats</em> across the network.
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sentiment chart */}
        <div className="lg:col-span-2 bg-[#121214] border border-white/5 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">apex storm risk index</div>
              <div className="font-display text-xl text-white">National severity · realtime</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="live-dot" />
              <span className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-300">streaming · NWS feed</span>
            </div>
          </div>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.chart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="t" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 11 }}
                  itemStyle={{ color: "#fff" }}
                />
                <Line type="monotone" dataKey="v" stroke="#dc2626" strokeWidth={1.6} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-white/5">
            <div>
              <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Now</div>
              <div className="font-mono-apex text-2xl text-[#dc2626]">ELEVATED</div>
            </div>
            <div>
              <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Peak · 24h</div>
              <div className="font-mono-apex text-2xl text-white">SEVERE</div>
            </div>
            <div>
              <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Forecast · 48h</div>
              <div className="font-mono-apex text-2xl text-zinc-300">MODERATE</div>
            </div>
          </div>
        </div>

        {/* Active threats list */}
        <div className="bg-[#121214] border border-white/5 p-5">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">active threats</div>
          <ul className="space-y-3 max-h-[440px] overflow-y-auto scroll-thin pr-2" data-testid="tickers-list">
            {data.tickers.map((t, i) => {
              const Icon = KIND_ICON[t.kind] || AlertTriangle;
              const sev = t.change;
              const high = sev >= 4;
              return (
                <li key={i} className="flex items-center gap-3 border-b border-white/5 pb-3 last:border-0">
                  <Icon size={18} strokeWidth={1.5} className={high ? "text-[#dc2626]" : "text-zinc-300"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-apex text-[11px] text-white truncate">{t.symbol}</div>
                    <div className="font-mono-apex text-[10px] text-zinc-500 flex items-center gap-1">
                      <MapPin size={9} /> {t.price}
                    </div>
                  </div>
                  <div className={`font-mono-apex text-xs ${high ? "text-[#dc2626]" : "text-zinc-400"}`}>
                    {sev > 0 ? "L" + Math.min(5, Math.round(sev)) : "↓"}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Damage zone heatmap */}
      <div className="bg-[#121214] border border-white/5 p-5">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">damage probability heatmap · 48h</div>
            <div className="font-display text-xl text-white">Where the work is coming from</div>
          </div>
          <div className="flex items-center gap-3 font-mono-apex text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#dc2626]" /> high</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-yellow-500/60" /> elevated</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500/40" /> low</span>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2">
          {[
            ["TX", 4.6], ["FL", 4.8], ["LA", 4.2], ["OK", 4.9], ["KS", 3.8], ["CO", 3.4], ["IL", 3.6], ["IA", 2.4],
            ["MO", 3.2], ["AR", 2.8], ["MS", 3.1], ["AL", 3.5], ["GA", 2.6], ["NC", 3.7], ["SC", 2.9], ["TN", 2.3],
            ["KY", 1.9], ["IN", 2.1], ["OH", 1.6], ["MI", 1.4], ["NE", 2.5], ["SD", 1.8], ["ND", 1.2], ["MN", 1.5],
          ].map(([state, score], i) => {
            const intensity = Math.min(score / 5, 1);
            const high = score >= 4;
            const mid = score >= 2.5 && score < 4;
            const bg = high
              ? `rgba(220,38,38,${0.25 + intensity * 0.45})`
              : mid
              ? `rgba(245,158,11,${0.18 + intensity * 0.35})`
              : `rgba(16,185,129,${0.12 + intensity * 0.25})`;
            return (
              <div
                key={i}
                className="p-3 border border-white/5 flex items-center justify-between min-h-[60px]"
                style={{ background: bg }}
                data-testid={`state-${state}`}
              >
                <span className="font-mono-apex text-sm text-white">{state}</span>
                <span className={`font-mono-apex text-[10px] ${high ? "text-white" : "text-zinc-200"}`}>
                  {score.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Forecast snapshots */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { Icon: Tornado, t: "Tornado watches", v: "5", d: "OK · KS · MO" },
          { Icon: AlertTriangle, t: "Hurricane track", v: "Cat 2", d: "FL panhandle · 18h to landfall" },
          { Icon: Thermometer, t: "Hail probability", v: "78%", d: "Denver Metro · next 4h" },
        ].map((c, i) => (
          <div key={i} className="bg-[#121214] border border-white/5 p-6">
            <c.Icon size={20} strokeWidth={1.5} className="text-[#dc2626] mb-4" />
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{c.t}</div>
            <div className="font-mono-apex text-3xl text-white mb-1">{c.v}</div>
            <div className="font-mono-apex text-[10px] text-zinc-500">{c.d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
